import React, { useState, useCallback, useRef, useEffect } from 'react';
import ModalContainer from 'Components/Common/Modal/ModalContainer';
import { toast } from 'react-toastify';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { LoadingState } from './DefineModel/model';
import { uploadProductModel } from './DefineModel/ModelServices/ModalServices';
import Label from 'Components/Common/components/Label';
import GMSButton from 'Components/Common/components/GMSButton';
import GMSFillButton from 'Components/Common/components/GMSFillButton';
import { XIcon } from 'assests/GmsIcons';
import LoadingSuccessErrorAlert from 'Components/GMS/Rules/Components/LoadingSuccessErrorAlert';

const API_BASE_URL = process.env.REACT_APP_GMS_BACKEND_API_URL || '';

type UploadModalProps = {
    id: string;
    showModal: boolean;
    closeModal: () => void;
    channelKey: string;
    onUploadSuccess?: () => void;
};

export const UploadModal = ({
    id,
    showModal,
    closeModal,
    channelKey,
    onUploadSuccess
}: UploadModalProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [inputKey, setInputKey] = useState('');
    const [loading, setLoading] = useState<LoadingState>({
        status: '',
        message: ''
    });

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const resetFileInput = useCallback(() => {
        const randomString = Math.random().toString(36);
        setInputKey(randomString);
    }, []);

    const handleFileSelection = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];

            if (!file) {
                setSelectedFile(null);
                return;
            }

            if (!file.name.endsWith('.xlsx')) {
                toast.error('Only .xlsx files are allowed');
                setSelectedFile(null);
                return;
            }

            setSelectedFile(file);
        },
        []
    );

    const handleFileDelete = useCallback(() => {
        setSelectedFile(null);
        resetFileInput();
    }, [resetFileInput]);

    const checkUploadStatus = useCallback(async (job_id: string) => {
        if (!isMountedRef.current || !job_id) return;

        try {
            const statusResponse = await ServiceWrapper.doGet(
                `${API_BASE_URL}job/status/${job_id}`,
                null,
                null,
                AuthManager.getGmsToken()
            );

            if (!statusResponse.errorfound) {
                const { data } = statusResponse;

                if (data?.error) {
                    toast.error(data.status || 'Upload failed');
                    setLoading({
                        status: 'Error',
                        message: data.status || 'Upload failed'
                    });
                    return;
                }

                if (data?.processing) {
                    setLoading({
                        status: 'Loading',
                        message: data.status || 'Processing...'
                    });

                    setTimeout(() => {
                        checkUploadStatus(job_id);
                    }, 2000);
                    return;
                }

                // Upload completed
                toast.success(data.status || 'Upload completed successfully');
                setLoading({
                    status: 'Success',
                    message: data.status || 'Upload completed successfully'
                });

                if (onUploadSuccess) {
                    onUploadSuccess();
                }

                setTimeout(() => {
                    if (isMountedRef.current) {
                        closeModal();
                        setLoading({ status: '', message: '' });
                    }
                }, 2000);
            } else {
                toast.error('Error checking upload status');
                setLoading({
                    status: 'Error',
                    message: 'Error checking upload status'
                });
            }
        } catch (error) {
            console.error('Error occurred while checking upload status:', error);
            toast.error('Error during upload');
            setLoading({
                status: 'Error',
                message: 'Error during upload'
            });
        }
    }, [closeModal, onUploadSuccess]);

    const handleUpload = useCallback(async () => {
        if (!selectedFile) {
            toast.error('Please select a file');
            return;
        }

        setLoading({
            status: 'Loading',
            message: 'File uploading...'
        });

        const result = await uploadProductModel(selectedFile, channelKey);

        if (!result.done || !result.job_id) {
            toast.error(result.error || 'Error uploading file');
            setLoading({
                status: 'Error',
                message: result.error || 'Error uploading file'
            });
            resetFileInput();
            setSelectedFile(null);
            return;
        }

        const { done, message, job_id } = result;

        if (!done) {
            toast.error(message);
            setLoading({
                status: 'Error',
                message
            });
            resetFileInput();
            setSelectedFile(null);
            return;
        }

        resetFileInput();
        setSelectedFile(null);

        if (job_id) {
            setTimeout(() => {
                checkUploadStatus(job_id);
            }, 2000);
        } else {
            toast.error('Job ID not found');
            setLoading({
                status: 'Error',
                message: 'Job ID not found'
            });
        }
    }, [selectedFile, channelKey, resetFileInput, checkUploadStatus]);

    const handleClose = useCallback(() => {
        if (loading.status === 'Loading') {
            toast.warning('Upload in progress. Please wait...');
            return;
        }

        setSelectedFile(null);
        setLoading({ status: '', message: '' });
        resetFileInput();
        closeModal();
    }, [loading.status, closeModal, resetFileInput]);

    const isUploading = loading.status === 'Loading';

    return (
        <ModalContainer
            isOpen={showModal}
            setOpen={handleClose}
            clickOutSideClose={!isUploading}
            width='500px'
        >
            {/* Modal Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b">
                <div className="tw-font-semibold tw-text-sm">Upload XLS</div>
                <button
                    type="button"
                    className="tw-bg-transparent tw-border-0 tw-text-2xl tw-leading-none tw-font-semibold tw-opacity-50 hover:tw-opacity-75 tw-cursor-pointer"
                    onClick={handleClose}
                    disabled={isUploading}
                    aria-label="Close"
                >
                    <XIcon />
                </button>
            </div>

            <div className='tw-px-3 tw-mt-6'>
                <div className="p-5 border cursor-pointer">
                    <div>
                        <label
                            className="cursor-pointer upload-area"
                            style={{
                                width: "100%",
                                textAlign: "center",
                                color: "#091caa",
                            }}
                        >
                            <input
                                type="file"
                                id={`${id}-file-input`}
                                className="tw-hidden"
                                key={inputKey || ''}
                                onChange={handleFileSelection}
                                onClick={(event) => {
                                    (event.target as HTMLInputElement).value = '';
                                }}
                                accept=".xlsx"
                                disabled={isUploading}
                                data-testid={`${id}-file-input`}
                            />
                            <i className="fa fa-cloud-upload fa-x"></i>
                            <br />
                            <Label
                                message="(Note: Only .xlsx files are allowed to upload)"
                                fontSize={"0.75"}
                                color={"#172B4D"}
                            />
                            <br />
                            <strong>Browse File</strong>
                        </label>
                        <br />
                        <span
                            className="mt-2 w-100"
                            style={{
                                display: "block",
                                width: "100%",
                                textAlign: "center",
                                wordWrap: "break-word",
                                marginLeft: "auto",
                                marginRight: "auto",
                            }}
                        >
                            {selectedFile && selectedFile.name}
                        </span>
                    </div>
                </div>
            </div>

            {/* Modal Footer */}
            <div className="tw-flex tw-items-center tw-justify-between tw-pb-8 tw-px-3">

                <div className="mt-3 d-flex justify-content-between w-100">
                    <button
                        className={"btn btn-outline-danger focus:outline-none active:scale-96"}
                        id={id}
                        disabled={!selectedFile}
                        onClick={handleFileDelete}
                    >
                        {"Delete"}
                    </button>

                    <div className="d-flex">
                        <GMSButton name="Close" onClick={() => handleClose()} />
                        &nbsp;&nbsp;&nbsp;
                        <GMSFillButton
                            name="Save"
                            disabled={!selectedFile || isUploading}
                            onClick={() => handleUpload()}
                        />
                    </div>
                </div>
            </div>
            <br />
            <br />
            <br />
            <div className="mt-2">
                <LoadingSuccessErrorAlert loading={loading} hide_error={true} handle_reload={null} />
            </div>
        </ModalContainer>
    );
};
